import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { interiorPages } from '../data/interiorPages';
const AUTOPLAY_DELAY = 6000;
const RESUME_DELAY = 9000;
export function InteriorCarousel() {
  const trackRef = useRef<HTMLUListElement>(null);
  const resumeTimer = useRef<number | undefined>(undefined);
  const [index, setIndex] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const scrollToIndex = useCallback((next: number) => {
    const track = trackRef.current;
    if (!track) return;
    const item = track.children[next] as HTMLElement | undefined;
    if (!item) return;
    track.scrollTo({
      left: item.offsetLeft - track.offsetLeft,
      behavior: 'smooth'
    });
  }, []);
  const pauseAutoplay = useCallback(() => {
    setAutoplay(false);
    window.clearTimeout(resumeTimer.current);
    resumeTimer.current = window.setTimeout(() => setAutoplay(true), RESUME_DELAY);
  }, []);
  const go = useCallback((direction: -1 | 1) => {
    pauseAutoplay();
    const next = (index + direction + interiorPages.length) % interiorPages.length;
    setIndex(next);
    scrollToIndex(next);
  }, [index, pauseAutoplay, scrollToIndex]);
  useEffect(() => {
    if (!autoplay) return;
    const timer = window.setInterval(() => {
      setIndex(current => {
        const next = (current + 1) % interiorPages.length;
        scrollToIndex(next);
        return next;
      });
    }, AUTOPLAY_DELAY);
    return () => window.clearInterval(timer);
  }, [autoplay, scrollToIndex]);
  useEffect(() => () => window.clearTimeout(resumeTimer.current), []);
  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const items = Array.from(track.children) as HTMLElement[];
    const closest = items.reduce((best, item, itemIndex) => {
      const distance = Math.abs(item.offsetLeft - track.offsetLeft - track.scrollLeft);
      return distance < best.distance ? {
        distance,
        itemIndex
      } : best;
    }, {
      distance: Infinity,
      itemIndex: 0
    });
    setIndex(closest.itemIndex);
  };
  return <div className="relative mt-6" data-id="element-93" data-mp="2qvil9u4725e9">
      <ul ref={trackRef} onScroll={handleScroll} onPointerDown={pauseAutoplay} onWheel={pauseAutoplay} className="-mx-5 flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth px-5 pb-2 sm:mx-0 sm:gap-4 sm:px-0 lg:gap-5" style={{
      scrollbarWidth: 'none'
    }} aria-label="Real pages from the Yoga Teacher System class library" data-id="element-94" data-mp="2qu7ermjd6jjg">
        {interiorPages.map((page, pageIndex) => <li key={page.src} className="w-[82%] shrink-0 snap-center sm:w-[46%] lg:w-[31.5%]" aria-current={pageIndex === index ? 'true' : undefined} data-id="element-95" data-mp="2lxh0n4r4dy0o" data-mpi={pageIndex}>
            <div className="overflow-hidden rounded-2xl border border-sandDark bg-ivory shadow-card sm:rounded-3xl" data-id="element-96" data-mp="166tl8rkr5da">
              <img src={page.src} alt={page.alt} className="block w-full" width={960} height={1200} loading={pageIndex < 2 ? 'eager' : 'lazy'} draggable={false} data-id="element-97" data-mp="3schpyrln3hya" />
            </div>
          </li>)}
      </ul>

      <div className="mt-3 flex items-center justify-center gap-4" data-id="element-98" data-mp="1576hwojwlyvo">
        <button type="button" onClick={() => go(-1)} aria-label="Previous page" className="hidden h-9 w-9 items-center justify-center rounded-full border border-sandDark bg-ivory text-forest transition-colors duration-150 ease-out hover:bg-cream focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold sm:flex" data-id="element-99" data-mp="1a4gcsjq2b2qw">
          <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-100" data-mp="1t0y08oxvoghc" />
        </button>

        <ul className="flex items-center gap-1.5" data-id="element-101" data-mp="2gqsglgheon6c">
          {interiorPages.map((page, pageIndex) => <li key={page.src} data-id="element-102" data-mp="2lkeicdm176nd" data-mpi={pageIndex}>
              <button type="button" onClick={() => {
            pauseAutoplay();
            setIndex(pageIndex);
            scrollToIndex(pageIndex);
          }} aria-label={`Go to page ${pageIndex + 1}`} aria-current={pageIndex === index ? 'true' : undefined} className={`block h-1.5 rounded-full transition-[width,background-color] duration-200 ease-out ${pageIndex === index ? 'w-5 bg-forest' : 'w-1.5 bg-sandDark'}`} data-id="element-103" data-mp="wx95509c28y2" />
            </li>)}
        </ul>

        <button type="button" onClick={() => go(1)} aria-label="Next page" className="hidden h-9 w-9 items-center justify-center rounded-full border border-sandDark bg-ivory text-forest transition-colors duration-150 ease-out hover:bg-cream focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold sm:flex" data-id="element-104" data-mp="16uwxjjjgv4d2">
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-105" data-mp="21wyyegvt7eeg" />
        </button>
      </div>

      <p className="mt-1 text-center text-[0.55rem] uppercase tracking-label text-muted sm:hidden" data-id="element-106" data-mp="26uqs9gg0lala">
        Swipe to see more pages
      </p>
    </div>;
}